<template>
  <div>
    <div class="card-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="custom-search">
          <b-form-group>
            <div class="d-flex align-items-center">
              <span class="text-nowrap"> {{ $t("rowsPerPage") }} </span>
              <b-form-select
                v-model="pageLength"
                :options="['25', '50', '100']"
                class="ml-1"
                @input="(value) => onPerPageChange({ pageLength: value })"
              />
            </div>
          </b-form-group>
        </div>
        <div class="d-flex align-items-center gap-2">
          <div>
            <input
              autocomplete="off"
              type="text"
              name="search"
              class="form-control"
              :placeholder="$t('search')"
              v-model="form.search_string"
            />
          </div>
          <button
            @click="
              $router.push({
                path: '/users/create',
                query: { companyId: $route.params.id },
              })
            "
            class="btn btn-primary d-flex align-items-center gap-1"
            v-if="$can('create-user')"
          >
            <feather-icon :icon="'AddIcon'" size="12" />
            <span>{{ $t("createUser") }}</span>
          </button>
        </div>
      </div>
    </div>
    <!--==================================-->
    <div class="dextop-table-view">
      <div class="table-responsive api-keys-table users-list-table">
        <!-- table -->
        <vue-good-table
          styleClass="vgt-table striped"
          :columns="columns"
          :fixed-header="false"
          :rows="modifiedUsers"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
            trigger: 'enter',
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
          }"
          mode="remote"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <!-- Column: Name -->
            <div v-if="props.column.field === 'name'" class="px-auto">
              <span>{{
                props.row.first_name + " " + props.row.last_name
              }}</span>
            </div>
            <span v-else-if="props.column.field === 'lastLogin'">
              {{ props.row.lastLogin && props.row.lastLogin.date ? formatLastLoginDate(props.row.lastLogin.date) : '' }}
            </span>
            <span v-else-if="props.column.field === 'roles'">
              <multiselect
                :disabled="!$can('update-user')"
                v-model="props.row.roles"
                label="title"
                @select="assignRoles($event, props.row.roles, props.row.id)"
                @remove="removeRoles($event, props.row.roles, props.row.id)"
                track-by="id"
                :options="roles"
                :multiple="true"
              >
              </multiselect>
            </span>

            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <feather-icon
                v-if="$can(`update-user`)"
                @click="$router.push(`/users/${props.row.id}/edit`)"
                size="16"
                icon="Edit2Icon"
                class="mr-50 cursor-pointer"
              />
              <feather-icon
                v-if="$can(`delete-user`) && rows.length > 1"
                @click="destroy(props.row.id)"
                size="16"
                icon="TrashIcon"
                class="mr-50 cursor-pointer"
              />
            </span>

            <!-- Column: Common -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-end flex-wrap">
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="totalRecords"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => onPageChange({ currentPage: value })"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
    <!--==================================-->
    <!--==================================-->
    <div class="reponsive-view-cards">
      <div class="row">
        <div class="col-md-4" v-for="user in modifiedUsers" :key="user.id">
          <div class="card mb-1">
            <div class="card-header">
              <h4 class="">{{ user.first_name }} {{ user.last_name }}</h4>
              <div class="d-flex gap-2">
                <feather-icon
                  class="cursor-pointer"
                  v-if="$can(`update-user`)"
                  @click="$router.push(`/users/${user.id}/edit`)"
                  size="18"
                  icon="Edit2Icon"
                />
                <feather-icon
                  class="cursor-pointer"
                  v-if="$can(`delete-user`) && rows.length > 1"
                  @click="destroy(user.id)"
                  size="18"
                  icon="TrashIcon"
                />
              </div>
            </div>
            <div class="card-body">
              <ul class="card-list">
                <li>
                  <div class="left">
                    <strong>{{ $t("email") }}</strong>
                  </div>
                  <div class="right">
                    <p>{{ user.email }}</p>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("city") }}</strong>
                  </div>
                  <div class="right">
                    <p>{{ user.city }}</p>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("street") }}</strong>
                  </div>
                  <div class="right">
                    <p>{{ user.street }}</p>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("zip") }}</strong>
                  </div>
                  <div class="right">
                    <p>{{ user.zip }}</p>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("roles") }}</strong>
                  </div>
                  <div class="right">
                    <multiselect
                      :disabled="!$can('update-user')"
                      v-model="user.roles"
                      label="title"
                      @select="assignRoles($event, user.roles, user.id)"
                      @remove="removeRoles($event, user.roles, user.id)"
                      track-by="id"
                      :options="roles"
                      :multiple="true"
                    />
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("lastLogin") }}</strong>
                  </div>
                  <div class="right">
                    <p>{{ user.lastLogin && user.lastLogin.date ? formatLastLoginDate(user.lastLogin.date) : '' }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-center mt-2">
        <b-pagination
          :value="1"
          :total-rows="totalRecords"
          :per-page="pageLength"
          first-number
          last-number
          align="right"
          prev-class="prev-item"
          next-class="next-item"
          class="mt-1 mb-0"
          @input="(value) => onPageChange({ currentPage: value })"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </div>
    <!--====================================-->
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import { debounce } from "@/utils/debounce";
import { mapGetters } from "vuex";
import Multiselect from "vue-multiselect";
import SearchFilter from "@/components/SearchFilter.vue";
import MultiSelectInput from "@/components/MultiSelectInput.vue";

export default {
  components: {
    PageHeader,
    Multiselect,
    SearchFilter,
    MultiSelectInput,
  },
  computed: {
    ...mapGetters("roles", ["roles"]),
    modifiedUsers() {
      return this.rows.map((row) => {
        return {
          ...row,
          roles: row.roles
            .map((roleId) => {
              return {
                ...(this.roles?.find((role) => role.id == roleId) ?? {}),
              };
            })
            .filter((role) => role.id),
          lastLogin: row.last_login || null,
        };
      });
    },
    columns() {
      return [
        {
          label: this.$t("name"),
          field: "name",
          sortable: false,
        },
        {
          label: this.$t("email"),
          field: "email",
          sortable: false,
        },
        {
          label: this.$t("city"),
          field: "city",
          sortable: false,
        },
        {
          label: this.$t("lastLogin"),
          field: "lastLogin",
          sortable: false,
        },
        {
          label: this.$t("street"),
          field: "street",
          sortable: false,
        },
        {
          label: this.$t("zip"),
          field: "zip",
          sortable: false,
        },
        {
          label: this.$t("roles"),
          field: "roles",
          sortable: false,
        },
        {
          tdClass: "vgt-center-align",
          thClass: "vgt-center-align white-color",
          label: this.$t("action"),
          field: "action",
          sortable: false,
        },
      ];
    },
    items() {
      return [
        // {
        //   text: this.$t("dentalTwin"),
        //   to: "/home",
        // },
        // {
        //   text: this.$t("users"),
        //   active: true,
        // },
      ];
    },
    optionalItems() {
      return {
        createBtn1: {
          show: true,
          text: this.$t("createUser"),
          icon: "PlusIcon",
          path: "/users/create",
          permission: "create-user",
        },
      };
    },
  },
  data() {
    return {
      totalRecords: 0,
      pageLength: 25,
      page: 1,
      toggleState: false,
      rows: [],
      searchTerm: "",
      form: {
        search_string: "",
        company_id: "",
      },
    };
  },
  async created() {
    this.debouncedFetch = debounce(async (newValue, oldValue) => {
      try {
        await this.loadItems();
      } catch (e) {
        console.error(e);
      }
    }, 300);
  },
  watch: {
    form: {
      handler() {
        this.debouncedFetch();
      },
      deep: true,
    },
  },

  async mounted() {
    try {
      await this.$store.dispatch("roles/list", {
        limit_start: 0,
        limit_count: 100,
      });
      await this.loadItems();
    } catch (e) {
      console.error("api error ___", e);
    }
  },

  methods: {
    formatLastLoginDate(dateString) {
      if (!dateString || dateString === null || dateString === undefined) return '';
      try {
        const date = new Date(dateString + ' UTC');
        return date.toLocaleDateString();
      } catch (error) {
        return dateString;
      }
    },
    truncateString(str, maxLength) {
      if (str.length > maxLength) {
        return str.substring(0, maxLength) + "...";
      } else {
        return str;
      }
    },
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this, newProps);
    },
    onPageChange(params) {
      this.page = params.currentPage;
      console.log(this.page);
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ pageLength: params.pageLength });
      this.loadItems();
    },
    async assignRoles(event, roles, id) {
      let arr = [];
      arr.push(event.id);
      roles.forEach((role) => {
        arr.push(role.id);
      });
      const payload = {
        roles: arr,
      };
      await this.$store.dispatch("users/update", {
        id: id,
        roles: payload.roles,
      });
    },
    async removeRoles(event, roles, id) {
      let arr = [];
      roles.forEach((role) => {
        arr.push(role.id);
      });
      arr.pop(event.id);
      const payload = {
        roles: arr,
      };
      await this.$store.dispatch("users/update", {
        id: id,
        roles: payload.roles,
      });
    },

    onSortChange(params) {
      this.sortType = params[0].type;
      this.sortField = params[0].field;
      this.loadItems();
    },
    resetFilter() {
      this.form = {
        search_string: "",
        company_id: "",
      };
      this.toggleState = false;
    },
    // load items is what brings back the rows from server
    async loadItems() {
      try {
        this.$store.commit("showLoader", true);
        let response = await this.$store.dispatch("users/list", {
          ...this.form,
          company_id: this.$route.params.id,
          limit_start: this.pageLength * (this.page - 1),
          limit_count: this.pageLength,
        });
        this.rows = response?.data?.data;
        this.totalRecords = response?.data?.count;
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
    async destroy(id) {
      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToDeleteThisRecord"),
        subText: this.$t("yesDeleteIt"),
      });

      if (confirmed) {
        await this.$store.dispatch("users/destroy", {
          id: id,
        });
        this.loadItems();
      }
    },
  },
};
</script>

<style>
#nprogress {
  position: relative;
  z-index: 9999999;
}

.white-color {
  color: white !important;
}

.vgt-responsive {
  overflow-x: visible !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
